import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaEdit, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { apiURL, filesURL } from 'config/globals'
import Topbar from 'components/Topbar'
import FormRow from 'components/FormRow'
import Footer from 'components/Footer'
import { checkAuth } from 'utils/Auth'
import { Table } from "react-bootstrap"

const CraftView = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [craftRecord, setCraftRecord] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [loadError, setLoadError] = useState('')
    const navigate = useNavigate()
    const { entityId, craftId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            await getCraft(craftId)
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getCraft = async (id) => {
        const response = await axios.get(`${apiURL}/get-craft-record/${id}`)

        if (!response.data.success) {
            setErrorMessage(response.data.error || response.data)
            return
        }

        setCraftRecord(response.data.item)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 300, height: 'fit-content'}}>
                {craftRecord.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/archive/crafts`} className="no-decoration"><FaAngleLeft /> Back to Crafts</Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>{craftRecord.name}</h1>
                                <div>
                                    {craftRecord.craftspersonName}{craftRecord.documetationDateTime && `, ${craftRecord.documetationDateTime.split('-')[0]}`}
                                    {" "}|{" "}
                                    {craftRecord.craftType}
                                    {" "}|{" "}
                                    #{craftRecord.inventoryNumber}
                                </div>
                            </div>
                        </header>
                        <Row>
                            <Col lg="10" xl="8" className="mb-3">
                                {craftRecord.mainVideo &&
                                    <video width="100%" controls>
                                        <source src={`${filesURL}/${craftRecord.mainVideo}`} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                }
                                <p className="mt-3">{craftRecord.shortDescription}</p>
                                <h3 className="mt-4 mb-2">Context</h3>
                                <Table striped bordered hover cellPadding="15">
                                    <tbody>
                                        <tr>
                                            <th width="50%">Documentation country</th>
                                            <td>{craftRecord.documetationCountry ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Documentation place</th>
                                            <td>{craftRecord.documetationPlace ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Documentation date</th>
                                            <td>{craftRecord.documetationDateTime ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Responsible person name</th>
                                            <td>{craftRecord.responsibleName ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Responsible person affiliation</th>
                                            <td>{craftRecord.responsibleAffiliation ?? '--'}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                                <h3 className="mt-4 mb-2">Craftsperson</h3>
                                <Table striped bordered hover cellPadding="15">
                                    <tbody>
                                        <tr>
                                            <th width="50%">Name</th>
                                            <td>{craftRecord.craftspersonName ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Role</th>
                                            <td>{craftRecord.craftspersonRole ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Production locality</th>
                                            <td>{craftRecord.productionLocality ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Place of production</th>
                                            <td>{craftRecord.productionPlace ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Method of learning the craft</th>
                                            <td>{craftRecord.learningMethodType ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Method of teaching the craft</th>
                                            <td>{craftRecord.teachingMethodType ?? '--'}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                                <h3 className="mt-4 mb-2">Materials and tools</h3>
                                <Table striped bordered hover cellPadding="15">
                                    <tbody>
                                        <tr>
                                            <th width="50%">Materials from natural environment</th>
                                            <td>{craftRecord.materialsFromNatural ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Materials from trade</th>
                                            <td>{craftRecord.materialsFromTrade ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Tools used for manufacture</th>
                                            <td>{craftRecord.toolsForManufacture ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Tools used for decoration</th>
                                            <td>{craftRecord.toolsForDecoration ?? '--'}</td>
                                        </tr>
                                        <tr>
                                            <th>Installations used</th>
                                            <td>{craftRecord.installations ?? '--'}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                                <h3 className="mt-4 mb-2">Process</h3>
                                <Table striped bordered hover cellPadding="15">
                                    <tbody>
                                        <tr>
                                            <th width="50%">Manufacturing techniques</th>
                                            <td>{craftRecord.manufacturingTechniquesTags ?? '--'}</td>
                                        </tr>
                                        {craftRecord.manufacturingTechniquesDesc &&
                                            <tr>
                                                <th></th>
                                                <td>{craftRecord.manufacturingTechniquesDesc ?? '--'}</td>
                                            </tr>
                                        }
                                        <tr>
                                            <th>Decoration</th>
                                            <td>{craftRecord.decorationTechniquesTags ?? '--'}</td>
                                        </tr>
                                        {craftRecord.decorationTechniquesDesc &&
                                            <tr>
                                                <th></th>
                                                <td>{craftRecord.decorationTechniquesDesc}</td>
                                            </tr>
                                        }
                                        {craftRecord.decorationTechniquesImage &&
                                            <tr>
                                                <th></th>
                                                <td><img src={`${filesURL}/${craftRecord.decorationTechniquesImage}`} width="100%" style={{maxWidth: 300}} /></td>
                                            </tr>
                                        }
                                        <tr>
                                            <th>Manufacture phases</th>
                                            <td>
                                                {craftRecord.manufacturingPhases
                                                    ? <ol>
                                                        {JSON.parse(craftRecord.manufacturingPhases).map(phase =>
                                                            <li>phase</li>
                                                        )}
                                                      </ol>
                                                    : <>--</>
                                                }
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                                <h3 className="mt-4 mb-2">Product and distribution</h3>
                                <Table striped bordered hover cellPadding="15">
                                    <tbody>
                                        <tr>
                                            <th width="50%">Products / artefacts made</th>
                                            <td>{craftRecord.productsMadeDesc ?? '--'}</td>
                                        </tr>
                                        {craftRecord.productsMadeImage &&
                                            <tr>
                                                <th></th>
                                                <td><img src={`${filesURL}/${craftRecord.productsMadeImage}`} width="100%" style={{maxWidth: 300}} /></td>
                                            </tr>
                                        }
                                        <tr>
                                            <th>Purpose of the products</th>
                                            <td>{craftRecord.productPurposeType ?? '--'}</td>
                                        </tr>
                                        {craftRecord.productPurposeType &&
                                            <tr>
                                                <th></th>
                                                <td>{craftRecord.productPurposeDesc}</td>
                                            </tr>
                                        }
                                        <tr>
                                            <th>Distribution</th>
                                            <td>{craftRecord.distributionType ?? '--'}</td>
                                        </tr>
                                        {craftRecord.distributionType === 'shops' &&
                                            <tr>
                                                <th></th>
                                                <td>{craftRecord.distributionShops ?? '--'}</td>
                                            </tr>
                                        }
                                        {craftRecord.distributionType === 'markets' &&
                                            <tr>
                                                <th></th>
                                                <td>{craftRecord.distributionMarkets ?? '--'}</td>
                                            </tr>
                                        }
                                        {craftRecord.distributionType === 'other' &&
                                            <tr>
                                                <th></th>
                                                <td>{craftRecord.distributionOther ?? '--'}</td>
                                            </tr>
                                        }
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                        
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                    </>
                }
                <Footer user={currentUser} />
            </main>
        </>
    )
}

export default CraftView